/** 可視かつ認証済みの管理画面で、現在端末の短命な状態だけを保持する。 */
import { useEffect, useState } from 'react';
import {
  LOCAL_MCP_STATUS_CHANGED_EVENT,
  localMcpDeviceStatus,
  probeLocalMcpStatus,
  type LocalMcpDeviceStatus,
} from '@/utils/localMcpStatus';

export const LOCAL_MCP_POLL_MS = 30_000;
export const LOCAL_MCP_FRESH_MS = 45_000;
const UNAVAILABLE: LocalMcpDeviceStatus = { kind: 'unavailable' };

export function useLocalMcpStatus(enabled: boolean, identity: string): LocalMcpDeviceStatus {
  const [snapshot, setSnapshot] = useState<{ identity: string; status: LocalMcpDeviceStatus }>({
    identity,
    status: UNAVAILABLE,
  });
  useEffect(() => {
    let disposed = false;
    let generation = 0;
    let controller: AbortController | null = null;
    let expiry: ReturnType<typeof setTimeout> | undefined;
    const clear = () => {
      generation += 1;
      controller?.abort();
      controller = null;
      clearTimeout(expiry);
      setSnapshot({ identity, status: UNAVAILABLE });
    };
    const check = async () => {
      if (!enabled || disposed || document.visibilityState !== 'visible') {
        if (!disposed) clear();
        return;
      }
      const requestGeneration = ++generation;
      controller?.abort();
      const request = new AbortController();
      controller = request;
      const result = await probeLocalMcpStatus('automatic', request.signal);
      if (
        disposed ||
        requestGeneration !== generation ||
        request.signal.aborted ||
        document.visibilityState !== 'visible'
      )
        return;
      clearTimeout(expiry);
      const status = localMcpDeviceStatus(result, Date.now());
      setSnapshot({ identity, status });
      if (status.kind !== 'unavailable') expiry = setTimeout(clear, LOCAL_MCP_FRESH_MS);
    };
    const refresh = () => {
      void check();
    };
    const visibility = () => {
      if (document.visibilityState === 'visible') refresh();
      else clear();
    };
    clear();
    if (enabled) refresh();
    const interval = enabled ? setInterval(refresh, LOCAL_MCP_POLL_MS) : undefined;
    window.addEventListener('focus', refresh);
    window.addEventListener(LOCAL_MCP_STATUS_CHANGED_EVENT, refresh);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      disposed = true;
      generation += 1;
      controller?.abort();
      clearInterval(interval);
      clearTimeout(expiry);
      window.removeEventListener('focus', refresh);
      window.removeEventListener(LOCAL_MCP_STATUS_CHANGED_EVENT, refresh);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [enabled, identity]);
  if (!enabled || snapshot.identity !== identity || document.visibilityState !== 'visible')
    return UNAVAILABLE;
  if (
    snapshot.status.kind !== 'unavailable' &&
    Date.now() - snapshot.status.checkedAt > LOCAL_MCP_FRESH_MS
  )
    return UNAVAILABLE;
  return snapshot.status;
}
